import styled from "styled-components"

import type { DetailAction, IntelligenceReportData } from "../../pdf/types"
import {
  formatActionTitle,
  getActionsByUrgency,
  hasNowOrSoonActions,
  LEGEND_ROWS,
  splitMonitorActions,
  URGENCY_BORDER_COLORS,
} from "../../sharedTokens"
import { MeasuredPaginator, type PaginatorItem } from "../MeasuredPaginator"
import { HeaderBar, HeaderSubtext, HeaderText, PageCard, PageContent, UrgencyBadge } from "../shared"

const LegendBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin: 0px 0 20px;
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`

const LegendMeaning = styled.div`
  font-size: 11px;
  color: #334155;
  flex: 1;
`

const UrgencyGroupContainer = styled.div`
  margin-bottom: 16px;
`

const UrgencyGroupBordered = styled.div<{ $borderColor: string }>`
  padding-left: 16px;
  border-left: 4px solid ${p => p.$borderColor};
`

const UrgencyGroupHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
`

const UrgencyGroupSubLabel = styled.div`
  font-size: 11px;
  color: #475569;
  margin-left: 8px;
`

const ActionItem = styled.div`
  margin-top: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f8fafc;

  &:nth-child(2) {
    margin-top: 9px;
  }
`

const ActionText = styled.div`
  font-size: 11px;
  line-height: 1.5;
  color: #334155;
`

const ActionTitleBold = styled.span`
  font-weight: 700;
  font-size: 11px;
  color: #0f172a;
`

const NoActionText = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  line-height: 1.42;
`

function ActionList({ items }: { items: DetailAction[] }) {
  return (
    <>
      {items.map((action, idx) => (
        <ActionItem key={idx}>
          <ActionText>
            <ActionTitleBold>{formatActionTitle(action.action)}: </ActionTitleBold>
            {action.description}
          </ActionText>
        </ActionItem>
      ))}
    </>
  )
}

function MonitorSubGroup({ borderColor, label, items }: { borderColor: string; label: string; items: DetailAction[] }) {
  return (
    <UrgencyGroupBordered $borderColor={borderColor}>
      <UrgencyGroupHeader>
        <UrgencyBadge code="monitor" />
        <UrgencyGroupSubLabel>{label}</UrgencyGroupSubLabel>
      </UrgencyGroupHeader>
      <ActionList items={items} />
    </UrgencyGroupBordered>
  )
}

interface Props {
  report: IntelligenceReportData
}

export function RecommendedActionsPage({ report }: Props) {
  const actionsByUrgency = getActionsByUrgency(report)
  const hasNowOrSoon = hasNowOrSoonActions(report)

  const items: PaginatorItem[] = [
    {
      id: "legend",
      node: (
        <LegendBox>
          {LEGEND_ROWS.map(row => (
            <LegendRow key={row.code}>
              <UrgencyBadge code={row.code} />
              <LegendMeaning>{row.meaning}</LegendMeaning>
            </LegendRow>
          ))}
        </LegendBox>
      ),
    },
  ]

  if (!hasNowOrSoon) {
    items.push({
      id: "no-action",
      node: (
        <UrgencyGroupContainer>
          <UrgencyGroupBordered $borderColor="#DCFCE7">
            <ActionItem style={{ marginTop: 0 }}>
              <NoActionText>
                No immediate or near-term action is indicated based on the current report; monitor during routine
                service.
              </NoActionText>
            </ActionItem>
          </UrgencyGroupBordered>
        </UrgencyGroupContainer>
      ),
    })
  }

  for (const group of actionsByUrgency) {
    const borderColor = URGENCY_BORDER_COLORS[group.code] || "#6366F1"

    if (group.code === "monitor") {
      const { diagnostic, routine } = splitMonitorActions(group.items)
      if (diagnostic.length > 0) {
        items.push({
          id: "monitor-diagnostic",
          node: (
            <UrgencyGroupContainer>
              <MonitorSubGroup borderColor={borderColor} label="Diagnostic/Context" items={diagnostic} />
            </UrgencyGroupContainer>
          ),
        })
      }
      if (routine.length > 0) {
        items.push({
          id: "monitor-routine",
          node: (
            <UrgencyGroupContainer>
              <MonitorSubGroup borderColor={borderColor} label="Routine Maintenance" items={routine} />
            </UrgencyGroupContainer>
          ),
        })
      }
    } else {
      items.push({
        id: `urgency-${group.code}`,
        node: (
          <UrgencyGroupContainer>
            <UrgencyGroupBordered $borderColor={borderColor}>
              <div style={{ marginBottom: 2 }}>
                <UrgencyBadge code={group.code} />
              </div>
              <ActionList items={group.items} />
            </UrgencyGroupBordered>
          </UrgencyGroupContainer>
        ),
      })
    }
  }

  return (
    <MeasuredPaginator
      items={items}
      renderPage={(nodes, idx) => (
        <PageCard key={idx}>
          <HeaderBar>
            <HeaderText>Recommended Actions (Execution Checklist)</HeaderText>
            <HeaderSubtext>These recommendations are based on the system condition described above.</HeaderSubtext>
          </HeaderBar>
          <PageContent>{nodes}</PageContent>
        </PageCard>
      )}
    />
  )
}
